/**
 * SF-4653: what the run made (from sf-api `created`) and what Eva checked afterwards (`checks`).
 * SF-4863: a done build with a failed check (fake data that failed or did not finish) says so
 * at the top of the page, not only in the checks list.
 */
import { Alert, List, Space, Tag, Typography } from 'antd';
import type { DemoBuildCheck, DemoBuildRunView } from '../api/demoBuildsClient';
import { madeSummary } from '../run';

export const RunMade: React.FC<{ created: DemoBuildRunView['created'] }> = ({ created }) => {
  const made = madeSummary(created);
  return (
    <Typography.Paragraph data-testid="run-made">
      {made.length ? made.join(', ') : 'Nothing was recorded as made.'}
    </Typography.Paragraph>
  );
};

export const RunChecks: React.FC<{ checks: DemoBuildCheck[] }> = ({ checks }) =>
  checks.length ? (
    <List
      size="small"
      dataSource={checks}
      rowKey={(c) => c.label}
      renderItem={(check) => (
        <List.Item data-testid="run-check" data-passed={String(check.passed)}>
          <Space direction="vertical" size={0}>
            <Space>
              <Tag color={check.passed ? 'success' : 'error'}>
                {check.passed ? 'Passed' : 'Failed'}
              </Tag>
              <Typography.Text>{check.label}</Typography.Text>
            </Space>
            {check.detail && <Typography.Text type="secondary">{check.detail}</Typography.Text>}
          </Space>
        </List.Item>
      )}
    />
  ) : (
    <Typography.Paragraph type="secondary">No checks were reported.</Typography.Paragraph>
  );

export const RunProblems: React.FC<{ checks: DemoBuildCheck[] }> = ({ checks }) => {
  const failed = checks.filter((c) => !c.passed);
  if (!failed.length) return null;
  return (
    <Alert
      type="warning"
      showIcon
      message="Done, but not everything worked"
      description={
        <Space direction="vertical" size={0}>
          {failed.map((check) => (
            <Typography.Text key={check.label}>
              {check.detail ? `${check.label}: ${check.detail}` : check.label}
            </Typography.Text>
          ))}
        </Space>
      }
    />
  );
};
